import { useServices } from '@/app/providers/ServiceContext';
import { useToast } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Loader2, Lock, X } from 'lucide-react';
import React, { useState, useMemo } from 'react';

interface AddPaymentMethodFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = ({ onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { billingRepo } = useServices();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || 'Error al procesar la tarjeta');
        return;
      }

      if (!paymentMethod) {
        setError('No se pudo crear el metodo de pago');
        return;
      }

      // Save to backend
      await billingRepo.addPaymentMethod({
        type: 'CARD',
        stripePaymentMethodId: paymentMethod.id,
        setAsDefault,
      });

      toast.success('Metodo de pago agregado correctamente');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Error al agregar el metodo de pago';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Datos de la tarjeta
        </label>
        <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1e293b',
                  '::placeholder': {
                    color: '#94a3b8',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <label className="flex items-center gap-2 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          className="w-4 h-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
        />
        <span className="text-sm text-slate-600">Establecer como metodo de pago predeterminado</span>
      </label>

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Lock className="w-4 h-4" />
        <span>Tus datos estan protegidos con encriptacion SSL</span>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading || !stripe}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Agregando...
            </>
          ) : (
            'Agregar tarjeta'
          )}
        </Button>
      </div>
    </form>
  );
};

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Check if Stripe key is configured
  const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

  // Only initialize Stripe when we have a valid key
  const stripePromise = useMemo(() => {
    if (stripeKey) {
      return loadStripe(stripeKey);
    }
    return null;
  }, [stripeKey]);

  if (!isOpen) return null;
  if (!stripeKey) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
          <div className="p-6 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-bold text-slate-800 mb-2">Stripe no configurado</h3>
            <p className="text-slate-500 text-sm mb-4">
              Para agregar metodos de pago, configura la variable de entorno VITE_STRIPE_PUBLISHABLE_KEY
            </p>
            <Button variant="secondary" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-bold text-slate-800">Agregar metodo de pago</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content with Stripe Elements */}
        <Elements stripe={stripePromise}>
          <AddPaymentMethodForm onClose={onClose} onSuccess={onSuccess} />
        </Elements>
      </div>
    </div>
  );
};

export default AddPaymentMethodModal;
